import {
  OrderTrackingOrderSource,
  orderTrackingOrderSources,
} from "@redotech/order-tracking-common/order-tracking-types";
import { Provider } from "@redotech/redo-order-common/provider";
import { TrackingPageEnvironment } from "../../context/tracking-page-context";

import { getProviderCapabilities } from "@redotech/redo-marketing-common/provider-capabilities";

// A Set widens the membership check to accept any Provider. Calling
// `.includes()` on the readonly tuple directly would reject a generic Provider,
// since TS narrows the tuple's element type to the specific literals.
const orderTrackingSourceSet: ReadonlySet<Provider> = new Set(
  orderTrackingOrderSources,
);

export function isOrderTrackingSource(
  provider: Provider,
): provider is OrderTrackingOrderSource {
  return orderTrackingSourceSet.has(provider);
}

/**
 * The provider the inspector treats as "active" for the product-upsell block.
 * The merchant's explicit choice on the block wins, then the team's default,
 * then Shopify if connected, then the first available provider.
 */
export function getEffectiveUpsellProvider({
  blockProvider,
  defaultProvider,
  availableProviders,
}: {
  blockProvider: Provider | null | undefined;
  defaultProvider: Provider | undefined;
  availableProviders: readonly Provider[];
}): Provider | undefined {
  if (
    blockProvider &&
    getProviderCapabilities(blockProvider).supportsProductRecommendations
  ) {
    return blockProvider;
  }
  if (
    defaultProvider &&
    getProviderCapabilities(defaultProvider).supportsProductRecommendations
  ) {
    return defaultProvider;
  }
  if (availableProviders.includes(Provider.SHOPIFY)) {
    return Provider.SHOPIFY;
  }
  const recommendationCapableAvailableProviders = availableProviders.filter(
    (provider) =>
      getProviderCapabilities(provider).supportsProductRecommendations,
  );
  if (recommendationCapableAvailableProviders.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return recommendationCapableAvailableProviders[0]!;
  }
  return undefined;
}

/**
 * Resolve the provider sent to `getRecommendedProductsByProvider`. In the
 * builder the preview order may come from a different provider than the page
 * being edited, so we mirror the inspector's effective-provider chain instead
 * of trusting `order.source`. Falls back to `order.source` when no valid
 * tracking-supported provider can be resolved (and always in the customer
 * portal, where the order is the real order).
 */
export function resolveRecommendationProvider({
  blockProvider,
  environment,
  defaultProvider,
  availableProviders,
  orderSource,
}: {
  blockProvider: Provider | null | undefined;
  environment: TrackingPageEnvironment;
  defaultProvider: Provider | undefined;
  availableProviders: readonly Provider[];
  orderSource: OrderTrackingOrderSource;
}): OrderTrackingOrderSource {
  // Prefer provider configured on upsell block
  if (
    blockProvider &&
    isOrderTrackingSource(blockProvider) &&
    getProviderCapabilities(blockProvider).supportsProductRecommendations
  ) {
    return blockProvider;
  }

  // Special case for the builder
  if (environment === TrackingPageEnvironment.BUILDER) {
    const effective = getEffectiveUpsellProvider({
      blockProvider: undefined,
      defaultProvider,
      availableProviders,
    });
    if (effective && isOrderTrackingSource(effective)) {
      return effective;
    }
  }

  // Check order source
  if (getProviderCapabilities(orderSource).supportsProductRecommendations) {
    return orderSource;
  }

  // Fall back to any available order tracking source that supports product recommendations
  const recommendationCapableAvailableProviders = availableProviders
    .filter(isOrderTrackingSource)
    .filter(
      (provider) =>
        getProviderCapabilities(provider).supportsProductRecommendations,
    );
  if (recommendationCapableAvailableProviders.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return recommendationCapableAvailableProviders[0]!;
  }

  console.error("No valid provider found for product recommendations");

  // Any available order tracking source at all
  const availableSources = availableProviders.filter(isOrderTrackingSource);
  if (availableSources.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return availableSources[0]!;
  }

  // Screw it, just use Shopify
  return Provider.SHOPIFY;
}
